import { Box, Button } from "@mui/material"
import { Plus } from "lucide-react"
import type { JSX } from "react"
import { useNavigate } from "react-router-dom"

import { useCan } from "@/auth/permissions"

import { ResellerDocumentsList } from "@crm-src/components/reseller-documents-list"

/**
 * The Quotes and Invoices tabs — ONE component, because they are the same shape.
 *
 * ONE direction (CEO, 2026-09-12): documents XOP addressed TO this reseller —
 * `bill_to_reseller_id` — and nothing else. Quotes and invoices go from XOP to
 * a reseller, never from a reseller to a customer, so the two "issued by this
 * reseller" lists and the "XOP to this reseller's customers" list are gone. A
 * quote the reseller REQUESTED on XOP Anytime lands here automatically (its CRM
 * copy carries the same `XQ-…` number and is locked to the reseller's
 * decision), and on /crm/quotes like any other quote.
 *
 * RAISING one (2026-09-13): the Invoices tab offers "New invoice", which opens
 * the ordinary generator at `?resellerId=` — addressed to the reseller, with no
 * customer and no agreement behind it. Curating money is `admin:users`, so the
 * button follows that and not the financial wall (which only decides whether
 * the tab is reachable at all). Quotes get no such button on purpose: a quote
 * to a reseller exists because the reseller REQUESTED it in the portal.
 *
 * BOTH tabs are behind the financial wall, and that is enforced OUTSIDE this
 * file: the detail page filters them out of the visible tab set for a viewer
 * without `crm:financials`, so this never mounts for one. Every figure still
 * renders through `<Money>` — defence in depth, and what `financial-wall.test.ts`
 * requires of any money on a finance-reachable page.
 */
export function ResellerMoneyTab({
  resellerId,
  kind,
}: {
  resellerId: string
  kind: "quote" | "invoice"
}): JSX.Element {
  const isQuote = kind === "quote"
  const navigate = useNavigate()
  const canRaise = useCan("admin:users")

  return (
    <Box data-cy={isQuote ? "reseller-quotes-tab" : "reseller-invoices-tab"} sx={{ display: "grid", gap: 3 }}>
      {!isQuote && canRaise && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            data-cy="new-reseller-invoice"
            startIcon={<Plus size={16} />}
            onClick={() => navigate(`/crm/invoices/generate?resellerId=${resellerId}`)}
          >
            New invoice
          </Button>
        </Box>
      )}
      <ResellerDocumentsList
        data-cy={isQuote ? "reseller-billed-quotes" : "reseller-billed-invoices"}
        title={isQuote ? "Quotes XOP issued to this reseller" : "Invoices XOP issued to this reseller"}
        entityNoun={isQuote ? "quotes" : "invoices"}
        emptyMessage={
          isQuote
            ? "XOP has not quoted this reseller yet — a quote the reseller requests on XOP Anytime appears here"
            : "XOP has not invoiced this reseller yet"
        }
        filters={{ document_type: kind, bill_to_reseller_id: resellerId }}
      />
    </Box>
  )
}
